import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import dayjs from "dayjs";
import { Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
// import MDTypography from "components/MDTypography";
import { TableCard, ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { LoadingDialog, ExportDialog, Error_ } from "ui-components/Alert";
import { SelectFields } from "ui-components/Form";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import { useCustomerValue } from "providers/CustomerContext";
import { useTeamValue } from "providers/TeamContext";
import { getDivisionGroupOptions } from "services/data.internal.service";
import { getDivisionBalanceCheckEnquiry } from "services/data.report.service";
import { DivisionBalanceCheckColumns } from "./columns";

// Explains the non-zero "Group Balance" of the Division Inventories report:
// for each SKU, the documents that moved only one side and the likely cause.
function DivisionBalanceCheck() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [divisionGroupOpt, setDivisionGroupOpt] = useState([]);
  const [selectedDivisionGroup, setSelectedDivisionGroup] = useState("");
  const { isDownloading, downloadCsv } = useCsvDownloadHook();

  // same access as the Division Inventories report
  const exportPermission =
    allowAll ||
    (Boolean(permissions["Division Inventories_Report"]) &&
      permissions["Division Inventories_Report"].findIndex((b) => b === "export") !== -1);

  useQuery({
    queryKey: ["division-group-options", { customerCode }],
    queryFn: () => getDivisionGroupOptions({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => setDivisionGroupOpt(data.data.data),
  });

  const { isFetching } = useQuery({
    queryKey: ["division-balance-check", { selectedDivisionGroup }],
    queryFn: () => getDivisionBalanceCheckEnquiry({ selectedDivisionGroup }),
    enabled: Boolean(selectedDivisionGroup),
    onSuccess: (data) => {
      const result = data.data.data;
      setData(result);
      setFilteredData(result);
    },
    onError: (data) => setAlert(<Error_ data={data} onChange={setAlert} />),
  });

  // const totals = filteredData.reduce(
  //   (acc, x) => {
  //     const b = Number(x.balance) || 0;
  //     if (b > 0) { acc.posSku++; acc.posQty += b; }
  //     if (b < 0) { acc.negSku++; acc.negQty += b; }
  //     return acc;
  //   },
  //   { posSku: 0, posQty: 0, negSku: 0, negQty: 0 }
  // );

  return (
    <ListLayout>
      {isFetching && <LoadingDialog />}
      {isDownloading && <ExportDialog />}
      {alert && <MDBox sx={{ my: 3 }}>{alert}</MDBox>}
      <TableCard sx={{ my: 3 }}>
        <MDBox margin="1rem 1rem 0 1rem">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={4}>
              <SelectFields
                field={{
                  name: "divisionGroup",
                  label: "Division Group",
                  value: selectedDivisionGroup,
                  errors: {},
                }}
                options={divisionGroupOpt}
                onChange={(e, option) => setSelectedDivisionGroup(option)}
              />
            </Grid>
            <Grid item xs={3}>
              <MDButton
                variant="gradient"
                color="info"
                disabled={!exportPermission || !filteredData.length}
                onClick={() =>
                  downloadCsv(
                    `${selectedDivisionGroup}_${dayjs().format("YYYYMMDD-HHmm")}_-Division-Balance-Check`,
                    filteredData,
                    DivisionBalanceCheckColumns.map((c) => ({ label: c.header, key: c.accessorKey }))
                  )
                }
              >
                Export
              </MDButton>
            </Grid>
            {/* <Grid item xs={5}>
              {Boolean(selectedDivisionGroup) && !isFetching && (
                <MDTypography variant="button" color="text">
                  Over (+): {totals.posSku} SKU / {totals.posQty} qty &nbsp;|&nbsp; Short (-):{" "}
                  {totals.negSku} SKU / {totals.negQty} qty
                </MDTypography>
              )}
            </Grid> */}
          </Grid>
        </MDBox>
        <BasicTable
          data={data}
          columns={DivisionBalanceCheckColumns}
          onFilterRowsChange={setFilteredData}
        />
      </TableCard>
    </ListLayout>
  );
}

export default DivisionBalanceCheck;
